import { useMemo } from 'react'
import { Medication, MedicationOld } from 'types/PrivateData/MedicalHistory/Medication'
import moment from 'moment'
import Badge, { BadgeVariant } from 'components/common/Badge'
import { medicationStatusesMap } from 'schemas/MedicalHistory'

const statusBadgeVariants: Record<string, BadgeVariant> = {
  [medicationStatusesMap.Current]: 'success',
  [medicationStatusesMap.Discontinued]: 'neutral',
  [medicationStatusesMap.Error]: 'error',
}

export const useMemberMedicationsTableColumns = (
  medications: (Medication | MedicationOld)[],
  { sortable = false, nameColumnTitle = 'Medication' } = {},
) => {
  const data = useMemo(
    () =>
      (medications || []).map((medication: Medication | MedicationOld) => {
        const isV2 = 'status' in medication

        if (!isV2) {
          const { strength, unitStrength, active } = medication || {}

          return {
            ...medication,
            strengthAndUnit: [strength, unitStrength].filter(Boolean).join(' '),
            status: active ? 'Current' : 'Discontinued',
          }
        }

        const { startDate, endDate } = medication

        return {
          ...medication,
          startDateSort: startDate ? new Date(startDate).valueOf() : null,
          endDateSort: endDate ? new Date(endDate).valueOf() : null,
        }
      }),
    [medications],
  )

  const columns = useMemo(
    () => [
      {
        id: 'name',
        header: nameColumnTitle,
        accessorKey: 'name',
        accessorFn: ({ name }: Medication) => name || '-',
      },
      {
        id: 'strengthAndUnit',
        header: 'Strength',
        accessorKey: 'strengthAndUnit',
        accessorFn: ({ strengthAndUnit }: Medication) => strengthAndUnit || '-',
        meta: {
          cellClassName: 'w-[115px]',
        },
      },
      {
        id: 'unit',
        header: 'Unit',
        accessorKey: 'unit',
        accessorFn: ({ unit }: Medication) => unit || '-',
        meta: {
          cellClassName: 'w-[115px]',
        },
      },
      {
        id: 'startDateSort',
        accessorKey: 'startDateSort',
        header: 'Start Date',
        cell: (cell: any) => {
          const { startDate } = cell?.row?.original || {}

          return startDate ? moment(startDate).utc().format('MM/DD/YYYY') : '-'
        },
        enableSorting: !!sortable,
        meta: {
          cellClassName: 'w-[115px]',
        },
      },
      {
        id: 'endDateSort',
        accessorKey: 'endDateSort',
        header: 'End Date',
        cell: (cell: any) => {
          const { endDate } = cell?.row?.original || {}

          return endDate ? moment(endDate).utc().format('MM/DD/YYYY') : '-'
        },
        enableSorting: !!sortable,
        meta: {
          cellClassName: 'w-[115px]',
        },
      },
      {
        id: 'lastUpdated',
        header: 'Last Updated',
        accessorKey: 'lastUpdated',
        accessorFn: ({ lastUpdatedBy, lastUpdatedByType }: Medication) =>
          lastUpdatedByType ? (lastUpdatedByType === 'member' ? 'Member' : lastUpdatedBy || '-') : '-',
        meta: {
          cellClassName: 'w-[115px]',
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        cell: (cell: any) => {
          const { status } = cell?.row?.original || {}

          if (!status) {
            return '-'
          }

          return <Badge variant={statusBadgeVariants[status]}>{status}</Badge>
        },
        enableSorting: !!sortable,
      },
    ],
    [nameColumnTitle, sortable],
  )

  return { data, columns }
}
